import { AnyAction } from 'redux';

import { STATE } from 'app/constants';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages/action-types';
import { blockAccess, isForbiddenStatus } from 'app/area/shared/actions-utils';
import { buildBreadcrumbs, getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { api as codeSelectorApi } from 'app/area/code-selector/api';

import { getActiveType } from './utils/get-active-type';
import { mapActionsToWire } from './utils/map-actions';
import { mapListBranchItemFromWire } from './utils/map-list-branch-item';
import { mapMetadataFromWire } from './utils/map-metadata';
import { getIncludeSubtables } from './utils/selected-value';
import {
  FailBreadcrumbs, ReceiveBreadcrumbs, RequestBreadcrumbs,
  RequestMetadata, ReceiveMetadata, FailMetadata,
  RequestAccessCodetables, ReceiveAccessCodetables, FailAccessCodetables,
  RequestListBranch, ReceiveListBranch, FailListBranch,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges,
  ClearList
} from './action-types';
import { api } from './api';
import { endpoints } from './endpoints';
import { defaultMaxResults, linkCodesTranslation as text } from './constants';
import { AccessCodetables, ActionPayloadType, ListAction, ListBranchModel } from './types-store';
import { breadcrumbsValidPath } from 'app/area/link-codes/utils/breadcrumbs-valid-path';

export const actionCreators = {

  loadBreadcrumbs: (breadcrumbs: number[], administrationCentral: boolean): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      const linkCodesBlockAccess = () => blockAccess({
        dispatch,
        getState,
        area: STATE.linkCodes,
        translationId: text.no_access_to_codetable_administration
      });

      if (!administrationCentral) {
        linkCodesBlockAccess();
        return;
      }

      dispatch(new RequestBreadcrumbs());

      try {
        const codetables = await codeSelectorApi.codetableAdministrationList();
        const codetablesOptions = codetables?.rootCodes?.map(codeItemToStoreValue) ?? [];
        const breadcrumbsOptions = buildBreadcrumbs(codetablesOptions, breadcrumbs);
        const activeType = getActiveType(getCodetableId(breadcrumbs));

        if (!breadcrumbsValidPath(breadcrumbsOptions)) {
          linkCodesBlockAccess();
          return;
        }

        dispatch(new ReceiveBreadcrumbs(
          breadcrumbsOptions,
          activeType.type,
          activeType.node
        ));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          linkCodesBlockAccess();
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailBreadcrumbs());
      }
    },

  loadMetadata: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      if (!isDefinedIncludingZero(id)) {
        return;
      }

      dispatch(new RequestMetadata(id));

      try {
        const metadataWire = await api.loadMetadata(id);
        const metadata = mapMetadataFromWire(metadataWire);

        dispatch(new ReceiveMetadata(id, metadata));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailMetadata(id));
      }
    },

  loadCodetablesAccess: (codetableId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestAccessCodetables(codetableId));

      try {
        const result = await api.loadCodetablesAccess(codetableId);
        const codetablesAccess = (result && result.length > 0) ? result.find(f => f.id === codetableId) : {} as AccessCodetables;

        if (!codetablesAccess?.administrationCentral) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.linkCodes,
            translationId: text.no_access_to_codetable_administration
          });
        }

        dispatch(new ReceiveAccessCodetables(codetableId, codetablesAccess));
      } catch (err) {
        blockAccess({
          dispatch,
          getState,
          area: STATE.linkCodes,
          translationId: text.no_access_to_codetable_administration
        });
        dispatch(new FailAccessCodetables(codetableId));
      }
    },

  loadListBranch: (
    model: ListBranchModel, id: number = 0,
    startIndex: number = 0, maxResults: number = defaultMaxResults
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestListBranch(model, id));

      try {
        const listBranch = await api.loadListBranch(model, id, startIndex, maxResults, getIncludeSubtables(model.activeType));

        const listBranchItems = mapListBranchItemFromWire(listBranch.results);

        const numberOfResults = listBranch.totalNumberOfResults;
        const clearList = startIndex === 0;

        dispatch(new ReceiveListBranch(model, id, listBranchItems, numberOfResults, clearList));

      } catch (err) {

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailListBranch(model, id));
      }
    },

  saveChanges: (
    model: ListBranchModel,
    activeValue: ComplexValue<number>,
    activeType: ActionPayloadType,
    listActions: ListAction[],
    successMessage: string,
    errorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestSaveChanges(model, activeValue, activeType));

      try {

        await api.saveChanges(
          mapActionsToWire(listActions),
          {
            [ActionPayloadType.userGroup]: endpoints.saveActionsUserGroup(model.codetable, activeValue?.value),
            [ActionPayloadType.type]: endpoints.saveActionsType(model.codetable, activeValue?.value)
          }[activeType] ?? ''
        );

        dispatch(new ReceiveSaveChanges(model, activeValue, activeType));
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        const actionsErrors = Object.keys(err?.validationErrors ?? {}).length > 0
          ? complexValidationFromServer(err.validationErrors)?.actions?.messages ?? []
          : [];

        const activeValueError = err?.validationErrors?.payload
          && complexValidationFromServer(err.validationErrors)?.payload?.messages?.[0];

        activeValueError && dispatch(new ErrorNotification(errorMessage));

        dispatch(new FailSaveChanges(model, activeValue, activeType, actionsErrors, activeValueError));
      }
    },

  clearList: (model: ListBranchModel): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      dispatch(new ClearList(model));
    }
};
